'use client'

import { StopAlertIcon } from '@/components/icons/StopAlertIcon'
import { FullScreenDialog } from '@/components/ui/dialog/FullScreenDialog'
import { cn } from '@/lib/utils'
import { ReactNode, useEffect, useRef, useState } from 'react'

export type ErrorDialogProps = {
  open?: boolean
  error?: unknown
  title: ReactNode
  details?: ReactNode
  errorId?: string
  onClose?: () => unknown
  className?: string
}

export function ErrorDialog({
  open,
  error,
  title,
  details,
  errorId,
  onClose,
  className,
}: ErrorDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const prevActiveElementRef = useRef<HTMLOrSVGElement>()
  const acceptButtonRef = useRef<HTMLButtonElement>(null)
  const [opened, setOpened] = useState(false)
  useEffect(() => {
    if (open) {
      setOpened(true)
    }
  }, [error, open])
  useEffect(() => {
    if (opened) {
      prevActiveElementRef.current =
        (document.activeElement as HTMLOrSVGElement | null) ?? undefined
      acceptButtonRef.current?.focus()
    } else {
      prevActiveElementRef.current?.focus?.()
    }
  }, [opened])
  return (
    <FullScreenDialog
      ref={dialogRef}
      open
      className={cn(
        'pointer-events-none z-50 opacity-0 transition-opacity',
        opened && 'pointer-events-auto opacity-100',
        className
      )}
    >
      <div
        className={cn(
          'grid items-center justify-center',
          'min-h-[max(600px,100%)] min-w-[max(375px,100%)]',
          'bg-black bg-opacity-40 p-5 transition-opacity',
          'text-[#181E25] dark:text-[#C4C4C4]'
        )}
      >
        <article className="flex w-full max-w-xl flex-col items-center rounded-xl bg-white p-5 shadow-lg drop-shadow-2xl dark:bg-[#2B2B2B]">
          <StopAlertIcon className="my-4 h-16 w-16 fill-[#CD1D32] dark:fill-[#FF6669]" />
          <h1 className="my-4 text-center text-4xl font-bold">{title}</h1>
          {details ? <p className="mb-4 text-center text-2xl font-normal">{details}</p> : null}
          {errorId ? <p className="mb-4 text-center text-2xl font-normal">{errorId}</p> : null}
          <button
            type="button"
            ref={acceptButtonRef}
            onClick={() => {
              setOpened(false)
              onClose?.()
            }}
            className="mt-8 self-stretch rounded bg-[#0860C4] px-4 py-3 text-base font-bold text-white dark:bg-[#1A94FF]"
          >
            OK
          </button>
        </article>
      </div>
    </FullScreenDialog>
  )
}
